import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { map, Observable, of } from 'rxjs';

import { ProductModelResponse } from '@app/core/models/api-response/product-response.model';
import { CustomerModel } from '@app/core/store/customer/customer.model';
import { SHOP_CHECKOUT_MOCKS } from '@app/features/cart-checkout/mocks/shop-checkout.mock';
import { CartAnalyticsModel } from '@app/features/cart-checkout/models/cart-analytics.model';
import { ShopCheckoutResponseApi } from '@app/features/cart-checkout/models/shop-checkout-response.model';
import { mapShopCheckoutResponse } from '@app/features/cart-checkout/utils/checkout-response.mapper';
import {
  MessageStickerResponseModel,
  MessageStickerCreateModel,
  MessageStickerUpdateModel
} from '@app/features/home/store/messages-sticker/message-sticker.model';
import {
  StudentModel,
  UpdateBiographyStudentPayload
} from '@app/features/student/store/student/student.model';
import { StudentSchoolsModel } from '@app/features/student/store/student-schools/student-schools.model';

import { environment } from '@env/environment';

import { BaseHttpService } from './base-http.service';

export interface StudentDataParams {
  studentIds: number[];
  schoolId: number;
  gradeId: number;
}

@Injectable({
  providedIn: 'root'
})
export class MyWorldHttpService {
  constructor(private readonly http: BaseHttpService) {}

  getStudent(studentToken: string) {
    return this.http.get<StudentModel>(`/family/${studentToken}`);
  }

  getStudentDataByName(studentName: string) {
    const params = new HttpParams().set('studentName', studentName);
    return this.http.get<StudentSchoolsModel>(`/family/name`, { params });
  }

  getStudentDataBySchoolAndGrade(studentParams: StudentDataParams) {
    let params = new HttpParams()
      .set('schoolId', studentParams.schoolId)
      .set('gradeId', studentParams.gradeId);

    studentParams.studentIds.forEach((id) => {
      params = params.append('studentIds', id);
    });

    return this.http.get<StudentModel>(`/family/match`, { params });
  }

  updateStudentBiography(pageId: number, data: UpdateBiographyStudentPayload) {
    const formData = new FormData();

    if (data.biography !== undefined && data.biography !== null) {
      formData.append('text', data.biography);
    }

    if (data.image) {
      formData.append('image', data.image);
    }

    return this.http.post<void>(`/pages/${pageId}/biography`, formData);
  }

  getProducts() {
    return this.http.get<ProductModelResponse[]>(`/Product`);
  }

  getProductsShowcase() {
    return this.http.get<ProductModelResponse[]>(`/Product/Showcase`);
  }

  initShopCheckout(data: CartAnalyticsModel) {
    const mock = environment.checkout.shopCheckoutMock;

    const source$: Observable<ShopCheckoutResponseApi> = mock?.enabled
      ? of(SHOP_CHECKOUT_MOCKS[mock.strategy])
      : this.http.post<ShopCheckoutResponseApi>(`/Family/Shop`, data);

    return source$.pipe(map((response) => mapShopCheckoutResponse(response)));
  }

  /** @deprecated Use initShopCheckout instead */
  getPaymentLink(data: CartAnalyticsModel) {
    return this.initShopCheckout(data).pipe(
      map((response) => response.redirectUrl ?? '')
    );
  }

  validateCustomerEmail(email: string) {
    return this.http.post<CustomerModel | null>(`/Family/validate/client`, {
      email
    });
  }

  registerCustomer(data: CustomerModel) {
    return this.http.post<CustomerModel>(`/Family/register/client`, data);
  }

  getMessageStickers() {
    return this.http.get<MessageStickerResponseModel[]>(`/Family/message`);
  }

  saveMessageSticker(messageSticker: MessageStickerCreateModel) {
    return this.http.post<MessageStickerResponseModel>(
      `/Family/message`,
      messageSticker
    );
  }

  editMessageSticker(messageData: MessageStickerUpdateModel) {
    return this.http.put(`/Family/message`, messageData);
  }

  deleteMessageSticker(id: number, writerEmail: string) {
    return this.http.delete(`/Family/message`, { body: { id, writerEmail } });
  }
}
